import {
  PERMISSIONS,
  ALL_PERMISSIONS,
  DEFAULT_MEMBER_PERMISSIONS,
  computeMemberPermissions,
} from '@/lib/permissions';
import { hasPermission } from '@/components/serverSettings/permissionConstants';
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { useGuildRoster } from '@/lib/useGuildRoster';
import type { Channel, GuildMemberRow, GuildRole } from '@/types/api';

/**
 * Pure permission helpers for a guild, mirroring the server's model
 * (owner bypass → role bitmask OR → ADMINISTRATOR bypass inside hasPermission).
 * Extracted so the message context menu, the user card mod-actions, etc. all
 * gate on the same logic without duplicating it.
 */

export function computeGuildPermissions(opts: {
  member: GuildMemberRow | null;
  rolesById: Map<string, GuildRole>;
  isOwner: boolean;
  /** Guild id — @everyone's role id by convention, and the baseline's source. */
  guildId?: string | null;
}): bigint {
  if (opts.isOwner) return ALL_PERMISSIONS;
  // @everyone is the baseline, not a constant: seeding with the constant made
  // the UI over-report whenever an owner restricted @everyone. Falls back to the
  // shipped default when the role isn't in the roster yet (a partial snapshot).
  const everyoneRole = opts.guildId ? opts.rolesById.get(opts.guildId) : undefined;
  const everyone = everyoneRole ? BigInt(everyoneRole.permissions) : null;
  if (!opts.member) return everyone ?? DEFAULT_MEMBER_PERMISSIONS;
  return computeMemberPermissions({
    everyone,
    roles: opts.member.roleIds
      .map((roleId) => opts.rolesById.get(roleId))
      .filter((role): role is GuildRole => !!role)
      .map((role) => BigInt(role.permissions)),
  });
}

/** Highest role position the member holds; owner outranks everyone. */
export function topRolePosition(
  roleIds: string[],
  rolesById: Map<string, GuildRole>,
  isOwner: boolean,
): number {
  if (isOwner) return Number.MAX_SAFE_INTEGER;
  return Math.max(0, ...roleIds.map((id) => rolesById.get(id)?.position ?? 0));
}

/**
 * May the current user moderate (kick/ban/role) the given member?
 * Can't touch the owner; otherwise owner can touch anyone, and a non-owner
 * can only touch members whose top role is strictly below their own.
 */
export function canManageMember(opts: {
  target: GuildMemberRow;
  rolesById: Map<string, GuildRole>;
  ownerId: string | null;
  myTopPosition: number;
  amOwner: boolean;
}): boolean {
  if (opts.ownerId && opts.target.userId === opts.ownerId) return false;
  if (opts.amOwner) return true;
  const targetTop = topRolePosition(opts.target.roleIds, opts.rolesById, false);
  return targetTop < opts.myTopPosition;
}

export interface MyGuildPermissions {
  permissions: bigint;
  isOwner: boolean;
  topRolePosition: number;
  can: (permission: bigint) => boolean;
}

/**
 * Current user's permissions in a guild. The server's `myPermissions` on the
 * guild is the answer when present (it follows role changes live and does not
 * depend on the roster's first page holding me); the roster computation is
 * the fallback for an older server. Rank still comes from the roster.
 */
export function useMyGuildPermissions(guildId: string | null | undefined): MyGuildPermissions {
  const myId = useAuthStore((s) => s.user?.id ?? null);
  const roster = useGuildRoster(guildId);
  const fromServer = useDataStore((s) => (guildId ? s.guilds[guildId]?.myPermissions : undefined));
  const ownerId = useDataStore((s) => (guildId ? s.guilds[guildId]?.ownerId : undefined)) ?? roster.ownerId;
  const isOwner = !!myId && !!ownerId && ownerId === myId;
  const permissions =
    fromServer !== undefined
      ? BigInt(fromServer)
      : computeGuildPermissions({ member: roster.me, rolesById: roster.rolesById, isOwner, guildId });
  return {
    permissions,
    isOwner,
    topRolePosition: topRolePosition(roster.me?.roleIds ?? [], roster.rolesById, isOwner),
    can: (permission: bigint) => hasPermission(permissions, permission),
  };
}

/**
 * May I do `bit` in this server or server channel? From the server's
 * `myPermissions` on it; when that is absent (a DM, an older server) the
 * answer is yes and the server decides — never block on missing data.
 */
export function canIn(scope: Pick<Channel, 'myPermissions'> | null | undefined, bit: bigint): boolean {
  if (!scope?.myPermissions) return true;
  return hasPermission(BigInt(scope.myPermissions), bit);
}

export { PERMISSIONS };
